import React, { useMemo } from 'react';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import { CalendarClock, Clock, ShieldCheck, Users } from 'lucide-react';
import { OOORequest, Role, User } from '../types';
interface ActiveOutOfOfficeProps {
  managedRole: Role | null;
  requests: OOORequest[];
  users: User[];
}
export function ActiveOutOfOffice({
  managedRole,
  requests,
  users
}: ActiveOutOfOfficeProps) {
  const activeRequests = useMemo(() => {
    const now = new Date();
    return requests.filter(request => {
      const isRequester = request.userRole === 'Processor' || request.userRole === 'Loan Officer';
      const isInScope = managedRole === null || request.userRole === managedRole;
      if (request.status !== 'Approved' || !isRequester || !isInScope) {
        return false;
      }
      return isWithinInterval(now, {
        start: parseISO(request.startDate),
        end: parseISO(request.endDate)
      });
    }).sort((a, b) => parseISO(a.endDate).getTime() - parseISO(b.endDate).getTime());
  }, [managedRole, requests]);
  const roleLabel = managedRole === null ? 'team members' : managedRole === 'Loan Officer' ? 'Loan Officers' : 'Processors';
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-6" data-id="element-61">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between" data-id="element-62">
        <div data-id="element-63">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-64">
            Active Out of Office
          </h2>
          <p className="mt-1 text-sm text-gray-600" data-id="element-65">
            See which {roleLabel.toLowerCase()} are currently away and who is
            covering.
          </p>
        </div>
        <div className="inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700" data-id="element-66">
          <span className="mr-2 h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" data-id="element-67" />
          {activeRequests.length} active now
        </div>
      </div>

      {activeRequests.length === 0 ? <section className="rounded-lg border border-gray-200 bg-white px-6 py-14 text-center shadow-sm" aria-labelledby="active-empty-title" data-id="element-68">
          <CalendarClock className="mx-auto mb-4 h-12 w-12 text-gray-300" aria-hidden="true" data-id="element-69" />
          <h3 id="active-empty-title" className="text-lg font-semibold text-gray-900" data-id="element-70">
            No one is currently out of office
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-71">
            Approved {roleLabel.toLowerCase()} absences that are active right
            now will appear here.
          </p>
        </section> : <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm" aria-labelledby="active-team-title" data-id="element-72">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4" data-id="element-73">
            <h3 id="active-team-title" className="text-lg font-medium text-gray-900" data-id="element-74">
              Away now
            </h3>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-75">
            {activeRequests.map(request => {
          const coverageNames = request.coverageIds?.map(id => users.find(user => user.id === id)?.name).filter((name): name is string => Boolean(name));
          return <li key={request.id} className="p-6" data-id="element-76">
                  <article className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-77">
                    <div className="min-w-0 flex-1" data-id="element-78">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-79">
                        <h4 className="text-lg font-semibold text-gray-900" data-id="element-80">
                          {request.userName}
                        </h4>
                        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700" data-id="element-81">
                          {request.userRole}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700" data-id="element-82">
                          Active now
                        </span>
                      </div>

                      <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded bg-gray-100 px-3 py-2 text-sm text-gray-700" data-id="element-83">
                        <Clock className="h-4 w-4 text-gray-500" aria-hidden="true" data-id="element-84" />
                        <span className="font-medium" data-id="element-85">
                          {format(parseISO(request.startDate), 'MMM d, yyyy h:mm a')}
                        </span>
                        <span className="text-gray-400" data-id="element-86">→</span>
                        <span className="font-medium" data-id="element-87">
                          {format(parseISO(request.endDate), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>

                      <p className="mt-3 rounded border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700" data-id="element-88">
                        {request.message}
                      </p>
                    </div>

                    <div className="w-full rounded-md border border-blue-100 bg-blue-50 p-4 lg:w-72" data-id="element-89">
                      <div className="flex items-center gap-2 text-sm font-medium text-blue-900" data-id="element-90">
                        <ShieldCheck className="h-4 w-4 text-[#0176d3]" aria-hidden="true" data-id="element-91" />
                        Coverage
                      </div>
                      {coverageNames && coverageNames.length > 0 ? <div className="mt-2 flex items-start gap-2 text-sm text-blue-800" data-id="element-92">
                          <Users className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-93" />
                          <span data-id="element-94">{coverageNames.join(', ')}</span>
                        </div> : <p className="mt-2 text-sm text-blue-700" data-id="element-95">
                          No coverage assignment needed.
                        </p>}
                    </div>
                  </article>
                </li>;
        })}
          </ul>
        </section>}
    </motion.div>;
}